import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useOffline } from './useOffline';
import { useSwal } from './useSwal';
import { useAppSelector } from '@/redux/hooks';
import connectivityStore from '@/redux/slices/connectivitySlice';
import {
  useLazyGetPosProductsQuery,
  useLazyGetPosCustomersWithoutPaginateQuery,
  useLazyGetPosSettingsQuery,
  useSyncPosSalesMutation,
  posApi,
} from '@/redux/api/posApi';

type IDBValidKey = any;

const DB_NAME = 'pos-offline-catalog';
// v4: warehouses / pos_settings / payment_methods / defaults stores — the POS
// page's ONLY source of warehouses, defaultWarehouse and defaultCustomer was
// the live GET pos/data_create_pos response, so an offline reload left the
// warehouse select empty and product search refusing ("select a warehouse
// first"). v3 kept products (keyed [warehouseId, id] since the v3 rebuild) /
// customers / pending_sales / settings.
const DB_VERSION = 4;

// Legacy synced queue rows are evicted after this age (they are only kept
// briefly for audit; fresh syncs delete their rows immediately).
const SYNCED_RECORD_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

// Sales per POST /sync request — the server caps a batch at 100
// (SyncController::MAX_SALES_PER_BATCH); stay under with headroom so one
// rejected batch can never wedge the whole queue.
const SYNC_BATCH_SIZE = 50;
// After this many conflict attempts a queued sale stops auto-retrying on
// every online event (it can never resolve by replay) — manual Sync Now
// still picks it up.
const MAX_AUTO_SYNC_ATTEMPTS = 5;
// Conflicts that ARE resolvable by replaying later (the server's own message
// says the sale stays queued until stock is replenished). They must not burn
// the auto-retry budget above, or the promise is a lie and a restocked sale
// silently parks until someone clicks Sync Now.
//
// `concurrency_conflict` belongs here too: it means another writer touched the
// same stock row between our read and our write (optimistic-lock version
// mismatch). The next attempt re-reads fresh state and normally succeeds, so
// treating it as permanent burned the budget on a transient race.
//
// `price_variance` is recoverable too (server marks it recoverable): cash was
// collected — parking it after 5 attempts strands money. Manual price update
// + Sync Now resolves it, auto-retry keeps it visible.
const RECOVERABLE_CONFLICT_REASONS = ['insufficient_stock', 'concurrency_conflict', 'price_variance'];

// Offline catalog freshness: products cached longer than this are treated as
// stale — prices/stock may have moved. The POS shows a freshness warning and
// forces a refresh on next online warehouse change.
export const CATALOG_STALE_MS = 24 * 60 * 60 * 1000;

export function isCatalogStale(lastCacheTime: number | null | undefined, now = Date.now()): boolean {
  if (!lastCacheTime) return true;
  return now - lastCacheTime > CATALOG_STALE_MS;
}

/**
 * Unique suffix for a queued sale's `localId`. The server dedupes on
 * (user, client sale id), so a collision would silently fold two DISTINCT sales
 * into one. `Math.random()` is not cryptographically unique and two terminals
 * can mint the same id inside one millisecond — prefer `crypto.randomUUID()`
 * and keep the Math.random() path only as a non-browser fallback.
 */
function createLocalIdSuffix(): string {
  const cryptoRef = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoRef && typeof cryptoRef.randomUUID === 'function') {
    return cryptoRef.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
}

// Exponential backoff between AUTOMATIC drain attempts after a failed drain
// (transport failure or new conflicts). Caps at 5 minutes; jitter spreads
// fleets of terminals so they don't hammer the recovering server in sync.
const AUTO_RETRY_BASE_MS = 15_000;
const AUTO_RETRY_MAX_MS = 5 * 60 * 1000;

// Drain lock is MODULE-scoped, not per-hook: usePosOffline is mounted twice on
// the POS page (Pos/index.tsx and PosCatalogRegion) and each instance
// registers its own window 'online' listener, so per-instance guards let two
// drains replay the same queued rows at once.
let syncInFlight = false;

// Backoff state for the background auto-drain (startBackgroundSync).
let autoRetryDelayMs = AUTO_RETRY_BASE_MS;
let autoRetryTimer: ReturnType<typeof setTimeout> | null = null;
let autoRetryScheduledInMs = 0;

function scheduleAutoRetry(): void {
  if (autoRetryTimer) return;
  const jitter = Math.round(autoRetryDelayMs * (0.5 + Math.random() * 0.5));
  autoRetryScheduledInMs = jitter;
  autoRetryTimer = setTimeout(() => {
    autoRetryTimer = null;
    autoRetryScheduledInMs = 0;
    startBackgroundSync();
  }, jitter);
  autoRetryDelayMs = Math.min(AUTO_RETRY_MAX_MS, autoRetryDelayMs * 2);
}

function resetAutoRetry(): void {
  autoRetryDelayMs = AUTO_RETRY_BASE_MS;
  autoRetryScheduledInMs = 0;
  if (autoRetryTimer) {
    clearTimeout(autoRetryTimer);
    autoRetryTimer = null;
  }
}

export function __resetAutoRetry(): void {
  resetAutoRetry();
}

/**
 * @internal Test/diagnostic seam: the delay of the currently pending
 * automatic retry (0 when no retry is scheduled).
 */
export function __getPendingAutoRetryDelayMs(): number {
  return autoRetryTimer ? autoRetryScheduledInMs : 0;
}

/**
 * The sale's calendar day in the TERMINAL's timezone.
 *
 * toISOString() is UTC: a terminal east of UTC selling after local midnight
 * stamped the previous day, and the backend books the sale on this date under
 * the fiscal-period guard — so the sale landed in the wrong day (and could be
 * tested against the wrong period).
 */
export function localDateString(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Queue ownership follows the uploader; origin attribution stays in data. */
export function createPendingSaleRecord(saleData: any, userId: any, receiptData: any = null): any {
  const now = new Date();
  return {
    localId: `offline-${now.getTime()}-${createLocalIdSuffix()}`,
    userId: userId ?? null,
    warehouseId: saleData?.warehouse_id ?? null,
    data: {
      ...saleData,
      date: saleData?.date || localDateString(now),
      client_created_at: saleData?.client_created_at || now.toISOString(),
    },
    receiptData,
    synced: false,
    createdAt: now.toISOString(),
    attempts: 0,
  };
}

/** Normalize Laravel's API envelope before saving the POS catalog offline. */
export function extractPosProductPage(responseData: any): { products: any[]; total: number } {
  const payload = responseData?.data ?? responseData;
  const products = Array.isArray(payload?.products)
    ? payload.products
    : Array.isArray(payload)
      ? payload
      : [];
  const reportedTotal = Number(payload?.total ?? payload?.totalRows);

  return {
    products,
    total: Number.isFinite(reportedTotal) ? reportedTotal : products.length,
  };
}

let activeDb: IDBDatabase | null = null;
let activeDbPromise: Promise<IDBDatabase> | null = null;

export function closeCatalogDB(): void {
  if (activeDb) {
    try {
      activeDb.close();
    } catch {
      // ignore
    }
    activeDb = null;
  }
  activeDbPromise = null;
}

function openCatalogDB(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB is not supported in this environment'));
  }
  if (activeDb) {
    return Promise.resolve(activeDb);
  }
  if (activeDbPromise) {
    return activeDbPromise;
  }
  activeDbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => {
      activeDbPromise = null;
      reject(request.error);
    };
    request.onsuccess = () => {
      const db = request.result;
      activeDb = db;
      activeDbPromise = null;
      db.onversionchange = () => {
        db.close();
        if (activeDb === db) activeDb = null;
      };
      db.onclose = () => {
        if (activeDb === db) activeDb = null;
      };
      resolve(db);
    };
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      const oldVersion = (event as IDBVersionChangeEvent).oldVersion;

      if (oldVersion < 3 && db.objectStoreNames.contains('products')) {
        // keyPath cannot be changed in place — drop and rebuild (cache is
        // rebuilt from the server on the next online preload).
        db.deleteObjectStore('products');
      }
      if (!db.objectStoreNames.contains('products')) {
        const store = db.createObjectStore('products', { keyPath: ['warehouseId', 'id'] });
        store.createIndex('warehouseId', 'warehouseId', { unique: false });
        store.createIndex('code', 'code', { unique: false });
        store.createIndex('categoryId', 'categoryId', { unique: false });
      }
      if (!db.objectStoreNames.contains('customers')) {
        db.createObjectStore('customers', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('pending_sales')) {
        const saleStore = db.createObjectStore('pending_sales', { keyPath: 'localId' });
        saleStore.createIndex('synced', 'synced', { unique: false });
        saleStore.createIndex('createdAt', 'createdAt', { unique: false });
      }
      if (!db.objectStoreNames.contains('settings')) {
        db.createObjectStore('settings', { keyPath: 'key' });
      }
      // v4 — cold-start usability stores (getElementsPOS payload slices).
      if (!db.objectStoreNames.contains('warehouses')) {
        db.createObjectStore('warehouses', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('pos_settings')) {
        db.createObjectStore('pos_settings', { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains('payment_methods')) {
        db.createObjectStore('payment_methods', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('defaults')) {
        db.createObjectStore('defaults', { keyPath: 'key' });
      }
    };
  });
  return activeDbPromise;
}

async function idbPut(storeName: string, data: any): Promise<any> {
  const db = await openCatalogDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const req = store.put(data);
    // Resolve on transaction COMMIT, not request success — resolving on
    // onsuccess could report a sale persisted while IndexedDB had not yet
    // auto-committed, losing it if the tab closed in that window.
    tx.oncomplete = () => resolve(req.result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

async function idbGetAll(storeName: string): Promise<any[]> {
  const db = await openCatalogDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(storeName: string, key: IDBValidKey): Promise<any> {
  const db = await openCatalogDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbDelete(storeName: string, key: IDBValidKey): Promise<void> {
  const db = await openCatalogDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    store.delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

async function idbGetByIndex(storeName: string, indexName: string, value: IDBValidKey): Promise<any[]> {
  const db = await openCatalogDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const index = store.index(indexName);
    const req = index.getAll(value);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Atomic product-cache rewrite for one warehouse: the eviction of the
 * warehouse's previous rows and the insert of the fresh rows happen in ONE
 * readwrite transaction. Previously delete and insert were separate
 * transactions, so dying between them left the cache empty — an offline
 * terminal with NO catalog until the next online preload.
 *
 * Eviction snapshots the existing keys FIRST (`getAllKeys`) and deletes them
 * by key. It must NOT iterate with a cursor that shares the transaction with
 * the inserts: the refreshed rows carry the same `warehouseId`, so a forward
 * cursor continued after the puts visited and deleted the rows this call had
 * just written — silently emptying the warehouse's offline catalog.
 *
 * `dbHandle` is injectable for unit tests (asserts delete+insert share a
 * single transaction); production passes nothing and opens the real DB.
 */
export async function rewriteWarehouseProducts(
  products: any[],
  warehouseId: string,
  dbHandle?: IDBDatabase,
): Promise<void> {
  const db = dbHandle ?? (await openCatalogDB());
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction('products', 'readwrite');
    const store = tx.objectStore('products');
    // Snapshot the warehouse's existing keys before inserting the fresh rows.
    const keysReq = store.index('warehouseId').getAllKeys(IDBKeyRange.only(warehouseId));
    keysReq.onsuccess = () => {
      for (const key of keysReq.result as IDBValidKey[]) {
        store.delete(key);
      }
      for (const product of products) {
        store.put({ ...product, warehouseId, cachedAt: Date.now() });
      }
    };
    keysReq.onerror = () => reject(keysReq.error);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/**
 * Product row lookup tolerant to id type drift (server ids are numeric, but
 * payloads can carry them as strings after a draft round-trip).
 */
function getProductRowStoreKey(id: any): IDBValidKey[] {
  return [id, Number(id), String(id)];
}

function toNonNegativeNumber(value: any): number {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : 0;
}

/**
 * Local stock decrement / restore (offline oversell guard). When a sale is
 * queued, the CACHED product quantity is decremented so the till reflects
 * queued-but-unsynced sales; discarding a queued sale re-increments it.
 * Adjusts `currentStock` plus the matching per-warehouse `warehouses[]`
 * entry, floored at 0 (never negative). Best-effort: a cache adjust failure
 * must never fail the queueing itself.
 *
 * Returns the product ids that hit the 0 floor on a decrement (oversell):
 * cash was already collected, so the sale still queues — but the caller
 * must surface an explicit "queued with insufficient cached stock, will
 * need replenishment before sync" warning instead of silently flooring.
 *
 * `dbHandle` is injectable for unit tests.
 */
export async function adjustCachedProductQuantities(
  warehouseId: any,
  lines: Array<{ productId: any; quantity: any }>,
  direction: 1 | -1,
  dbHandle?: IDBDatabase,
): Promise<{ oversoldProductIds: any[] }> {
  const relevant = lines.filter((l) => l?.productId != null && Number(l.quantity) > 0);
  if (!relevant.length) return { oversoldProductIds: [] };
  const db = dbHandle ?? (await openCatalogDB());
  if (!db.objectStoreNames.contains('products')) return { oversoldProductIds: [] };
  const normalizedWarehouseId = String(warehouseId);
  const oversoldProductIds: any[] = [];

  const tx = db.transaction('products', 'readwrite');
  const store = tx.objectStore('products');
  const committed = new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });

  // Row lookups tolerate id type drift (server ids are numeric, but payloads
  // can carry them as strings after a draft round-trip): all key candidates
  // for every line are looked up, first hit wins.
  //
  // IMPORTANT: every store.get is issued up front, in this same tick, and
  // each store.put is issued synchronously inside its get's onsuccess
  // handler. IndexedDB auto-commits a transaction the moment it has no
  // pending requests and control returns to the event loop — the previous
  // sequential await-chain (async reduce over candidates) could hit that
  // window when a row was missing (`continue` issued no new request) and
  // later store.put calls threw TransactionInactiveError, silently dropping
  // stock adjustments.
  const adjustedLines = new Set<number>();

  relevant.forEach((line, lineIndex) => {
    for (const key of getProductRowStoreKey(line.productId)) {
      // NaN (Number() of a non-numeric id) is not a valid IDB key and would
      // throw synchronously on store.get — skip such candidates.
      if (typeof key === 'number' && !Number.isFinite(key)) continue;
      const req = store.get([normalizedWarehouseId, key]);
      req.onsuccess = () => {
        if (req.result == null) return;
        if (adjustedLines.has(lineIndex)) return; // another candidate hit first
        adjustedLines.add(lineIndex);

        const row = req.result;
        const qty = Number(line.quantity);
        const entry = Array.isArray(row.warehouses)
          ? row.warehouses.find((w: any) => String(w?.warehouseId) === normalizedWarehouseId)
          : null;
        const current = toNonNegativeNumber(entry?.quantity ?? row.currentstock ?? row.currentStock ?? row.quantity);
        if (direction === -1 && current < qty) {
          oversoldProductIds.push(line.productId);
        }
        row.currentStock = String(Math.max(0, current + direction * qty));
        // The POS endpoint supplies `quantity`; older cache rows/tests used
        // `currentStock`. Keep both the tile and stock guard on the same value.
        row.quantity = row.currentStock;
        row.currentstock = row.currentStock;
        if (entry) entry.quantity = row.currentStock;
        store.put(row);
      };
      req.onerror = (e) => {
        // A failed lookup is a miss — swallow it so the adjust stays
        // best-effort and the transaction is not aborted.
        e.preventDefault?.();
      };
    }
  });

  await committed;
  return { oversoldProductIds };
}

/**
 * Reads one cached product's stock picture for the OFFLINE oversell guard.
 * Prefers the per-warehouse quantity when the row carries per-warehouse
 * stock; falls back to the product's total currentStock. Shaped like the
 * server's get_product_stock payload (currentstock/qteSale/qte) so the
 * existing stock guard messaging in usePosSession works unchanged offline.
 */
export async function readCachedProductStock(
  warehouseId: any,
  productId: any,
  dbHandle?: IDBDatabase,
): Promise<{ currentstock: number; qteSale: number; qte: number } | null> {
  if (productId == null) return null;
  const db = dbHandle ?? (await openCatalogDB());
  if (!db.objectStoreNames.contains('products')) return null;
  const normalizedWarehouseId = String(warehouseId);
  const tx = db.transaction('products', 'readonly');
  const store = tx.objectStore('products');
  const row = await new Promise<any>((resolve) => {
    let found: any = null;
    const requests = getProductRowStoreKey(productId).map((key) => store.get([normalizedWarehouseId, key]));
    requests.forEach((req) => {
      req.onsuccess = () => {
        if (!found && req.result) found = req.result;
      };
    });
    tx.oncomplete = () => resolve(found);
    tx.onerror = () => resolve(null);
    tx.onabort = () => resolve(null);
  });
  if (!row) return null;

  const perWarehouse = Array.isArray(row.warehouses)
    ? row.warehouses.find((w: any) => String(w?.warehouseId) === normalizedWarehouseId)
    : null;
  const qty = Math.max(
    0,
    toNonNegativeNumber(perWarehouse?.quantity ?? row.currentstock ?? row.currentStock ?? row.quantity),
  );
  return { currentstock: qty, qteSale: qty, qte: qty };
}

async function putKeyedRow(storeName: 'defaults' | 'pos_settings' | 'settings', key: string, value: any): Promise<void> {
  try {
    await idbPut(storeName, { key, value });
  } catch {
    // Cache writes are best-effort; a full quota must not break the POS.
  }
}

/**
 * Persists the getElementsPOS payload slices the POS page needs to boot
 * offline: warehouses, payment accounts (cash/bank), categories, brands and
 * the default warehouse/customer/products-per-page settings.
 */
export async function cachePosElements(payload: any): Promise<void> {
  if (!payload) return;
  try {
    const warehouses = Array.isArray(payload.warehouses) ? payload.warehouses : [];
    const accounts = Array.isArray(payload.accounts) ? payload.accounts : [];
    const db = await openCatalogDB();

    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(['warehouses', 'payment_methods', 'defaults'], 'readwrite');
      if (warehouses.length) {
        const whStore = tx.objectStore('warehouses');
        for (const warehouse of warehouses) whStore.put({ ...warehouse, id: warehouse.id });
      }
      if (accounts.length) {
        const pmStore = tx.objectStore('payment_methods');
        for (const account of accounts) pmStore.put({ ...account, id: account.id });
      }
      const defaults = tx.objectStore('defaults');
      defaults.put({ key: 'defaultCustomerName', value: payload.default_customer_name ?? payload.defaultCustomerName ?? null });
      defaults.put({ key: 'productsPerPage', value: payload.products_per_page ?? payload.productsPerPage ?? null });
      defaults.put({ key: 'categories', value: Array.isArray(payload.categories) ? payload.categories : [] });
      defaults.put({ key: 'brands', value: Array.isArray(payload.brands) ? payload.brands : [] });
      for (const key of ['defaultWarehouse', 'defaultCustomer', 'baseCurrency', 'currencies']) defaults.put({ key, value: payload[key] ?? null });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } catch {
    // Best-effort — offline boot simply falls back to whatever was cached.
  }
}

/**
 * Assembles the cached getElementsPOS slices for offline boot (Pos/index.tsx
 * hydrates the same state the live request populates).
 */
export async function getOfflinePosElements(): Promise<{
  warehouses: any[];
  paymentMethods: any[];
  categories: any[];
  brands: any[];
  defaultWarehouse: string | null;
  defaultCustomer: string | null;
  defaultCustomerName: string | null;
  productsPerPage: number | null;
  baseCurrency?: any;
  currencies?: any[];
}> {
  const [warehouses, paymentMethods, defaultRows] = await Promise.all([
    idbGetAll('warehouses'),
    idbGetAll('payment_methods'),
    idbGetAll('defaults'),
  ]);
  const readDefault = (key: string) => defaultRows.find((row: any) => row?.key === key)?.value ?? null;
  return {
    warehouses,
    paymentMethods,
    categories: readDefault('categories') ?? [],
    brands: readDefault('brands') ?? [],
    defaultWarehouse: readDefault('defaultWarehouse'),
    defaultCustomer: readDefault('defaultCustomer'),
    defaultCustomerName: readDefault('defaultCustomerName'),
    productsPerPage: readDefault('productsPerPage'),
    baseCurrency: readDefault('baseCurrency'),
    currencies: readDefault('currencies') ?? [],
  };
}

/** Caches the invoice `setting` block + currency symbol for offline receipts. */
export async function cacheReceiptSettings(setting: any, symbol: any): Promise<void> {
  if (setting) await putKeyedRow('pos_settings', 'invoiceSetting', setting);
  if (symbol != null) await putKeyedRow('pos_settings', 'symbol', symbol);
}

/** Reads the cached invoice settings (null when never fetched online). */
export async function readCachedReceiptSettings(): Promise<{ setting: any; symbol: string } | null> {
  try {
    const rows = await idbGetAll('pos_settings');
    const setting = rows.find((r: any) => r?.key === 'invoiceSetting')?.value ?? null;
    const symbol = rows.find((r: any) => r?.key === 'symbol')?.value;
    if (!setting && !symbol) return null;
    return { setting, symbol: symbol ?? '' };
  } catch {
    return null;
  }
}

/**
 * Logout cleanup — the offline stores are per browser profile, so without a
 * wipe the next login (possibly a different user or tenant) would inherit
 * stale catalog rows and, worse, replay the previous user's queued sales.
 */
export async function clearPosOfflineData(): Promise<void> {
  closeCatalogDB();
  try {
    await new Promise<void>((resolve) => {
      const request = indexedDB.deleteDatabase(DB_NAME);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
      request.onblocked = () => resolve();
    });
  } catch {
    // Best effort — a blocked deletion completes once open connections close.
  }
}

/**
 * Unsynced queue size for the logout guard — reads IndexedDB directly so it
 * works outside the hook (redux thunk on a logged-in session).
 */
export async function countUnsyncedPendingSales(userId?: any): Promise<number> {
  try {
    const sales = await idbGetAll('pending_sales');
    return sales.filter((s: any) => {
      return !s.synced && !(userId !== undefined && userId !== null && String(s.userId ?? '') !== String(userId));
    }).length;
  } catch {
    return 0;
  }
}

/**
 * Service-worker cache-clear path (CACHE_CLEARED on a version bump).
 *
 * Everything in the offline DB is regenerable from the server EXCEPT
 * `pending_sales`: those rows are money already collected from a customer and
 * cannot be recreated. The old handler called clearPosOfflineData() — a full
 * `deleteDatabase` — so a version bump while the drain had not yet run
 * destroyed queued sales silently, with no warning and no logout guard. Clear
 * the catalog/settings stores (which the SW has a real reason to invalidate),
 * keep the unsynced queue rows, and drop only the already-synced ones.
 */
export async function clearPosOfflineCatalog(): Promise<void> {
  const catalogStores = [
    'products',
    'customers',
    'settings',
    'warehouses',
    'pos_settings',
    'payment_methods',
    'defaults',
  ];

  try {
    const db = await openCatalogDB();
    await new Promise<void>((resolve) => {
      const tx = db.transaction([...catalogStores, 'pending_sales'], 'readwrite');
      catalogStores.forEach((name) => tx.objectStore(name).clear());

      const cursorRequest = tx.objectStore('pending_sales').openCursor();
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (!cursor) return;
        if (cursor.value?.synced) cursor.delete();
        cursor.continue();
      };

      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  } catch {
    // Best effort — a blocked transaction leaves the cache to the next reload.
  }
}

// ---------------------------------------------------------------------------
// Queue drain (module level so SystemInfrastructureProvider can mount it
// app-wide, not just the POS page's hooks).
// ---------------------------------------------------------------------------

export const MAX_ALLOWED_CLOCK_DRIFT_MINUTES = 5;

export type PosOfflineSyncEvent =
  | { type: 'sync-started' }
  | {
      type: 'sync-finished';
      syncedCount: number;
      failedConflicts: any[];
      transportFailed: boolean;
      clockDriftMinutes?: number;
    };

const syncEventListeners = new Set<(event: PosOfflineSyncEvent) => void>();

/** Subscribes to drain lifecycle events (hook state + toasts ride these). */
export function subscribePosOfflineSync(listener: (event: PosOfflineSyncEvent) => void): () => void {
  syncEventListeners.add(listener);
  return () => {
    syncEventListeners.delete(listener);
  };
}

function emitSyncEvent(event: PosOfflineSyncEvent): void {
  syncEventListeners.forEach((listener) => listener(event));
}

// Only the first listener to observe a finished drain reports it — the hook
// mounts twice on the POS page and both subscribe; without the claim every
// drain would toast twice.
let finishedToastsClaimed = false;
function tryClaimFinishedToasts(): boolean {
  if (finishedToastsClaimed) return false;
  finishedToastsClaimed = true;
  return true;
}

export interface DrainOptions {
  manual: boolean;
  userId: any;
  isOnline: boolean;
}

export interface DrainResult {
  ran: boolean;
  syncedCount: number;
  failedConflicts: any[];
  transportFailed: boolean;
  /** 401/403 on the batch: session expired or permission revoked — visible, not silent. */
  authFailed?: boolean;
  clockDriftMinutes?: number;
}

/**
 * Queue persistence/transport seams — production reads and writes the real
 * IndexedDB and replays through POST /sync; tests inject in-memory versions.
 */
export interface DrainDeps {
  loadQueue: () => Promise<any[]>;
  persistSale: (sale: any) => Promise<void>;
  deleteSale: (localId: string) => Promise<void>;
  syncBatch: (salesMap: Record<string, any>) => Promise<any>;
  getSale?: (localId: string) => Promise<any>;
}

const defaultDrainDeps: DrainDeps = {
  loadQueue: () => idbGetAll('pending_sales'),
  persistSale: (sale) => idbPut('pending_sales', sale),
  deleteSale: (localId) => idbDelete('pending_sales', localId),
  getSale: (localId) => idbGet('pending_sales', localId),
  syncBatch: async (salesMap) => {
    const { store } = await import('@/redux/store');
    return store.dispatch(posApi.endpoints.syncPosSales.initiate({ salesMap })).unwrap();
  },
};

/**
 * Runs the sale queue against the network in ONE drain pass.
 *
 * FIFO: queued sales are replayed in createdAt ASCENDING order — the oldest
 * sale is the one the customer paid for first, and stock deducted by an
 * earlier replay must be visible to the next (the old newest-first order
 * could reject a later line before an earlier one consumed the restock).
 *
 * Replay transport is POST /sync, which dedupes per (user, client sale id)
 * server-side — a lost HTTP response (e.g. success-but-timeout) cannot
 * double-charge stock or the AR ledger the way a blind re-POST to
 * /pos/create_pos could.
 *
 * Sent in chunks under the server's per-batch cap (SyncController
 * MAX_SALES_PER_BATCH = 100): one giant salesMap 422'd the whole request
 * once the queue passed the cap, wedging every future retry.
 */
export async function drainPendingSales(
  options: DrainOptions,
  deps: DrainDeps = defaultDrainDeps,
): Promise<DrainResult> {
  const noop: DrainResult = { ran: false, syncedCount: 0, failedConflicts: [], transportFailed: false };
  // Claim the drain SYNCHRONOUSLY, before any await. The Web Locks request
  // below yields, and while the lock is being acquired the flag would still
  // be false if it were only set inside runDrain — a second concurrent call
  // (double-mount, 'online' event, background sync) would pass this guard
  // and, in a single-tab/no-Web-Locks environment, both could reach
  // runDrain and replay the same pending_sales rows.
  if (!options.isOnline || syncInFlight) return noop;
  syncInFlight = true;

  // Cross-tab lock: navigator.locks queues a drain only when this tab can
  // actually take it ({ ifAvailable: true } resolves null otherwise).
  // Feature-detected — single-tab environments without Web Locks keep working
  // (the module-level syncInFlight guard still serializes this tab).
  const locks = typeof navigator !== 'undefined' ? (navigator as any).locks : undefined;
  try {
    if (locks?.request) {
      const handle = await locks.request('pos-offline-sync', { ifAvailable: true }, async (lock: unknown) => {
        if (!lock) return noop; // another tab is draining right now
        return runDrain(options, deps);
      });
      return handle ?? noop;
    }
    // Keep the claim until persistence and transport finish. Returning the
    // promise directly executes finally before runDrain settles.
    return await runDrain(options, deps);
  } finally {
    syncInFlight = false;
  }
}

async function runDrain(options: DrainOptions, deps: DrainDeps): Promise<DrainResult> {
  const noop: DrainResult = { ran: false, syncedCount: 0, failedConflicts: [], transportFailed: false };
  // syncInFlight is claimed by drainPendingSales (before its first await) —
  // see the comment there for the double-mount race.
  finishedToastsClaimed = false;

  {
    // Hydrate straight from the queue store — the hook may not have loaded
    // yet (fresh page load while online) and another tab may have mutated
    // the queue since this closure was created.
    const sales = await deps.loadQueue();

    const unsynced = sales.filter((s) => {
      if (s.synced) return false;
      if (String(s.userId ?? '') !== String(options.userId)) return false;
      if (options.manual) return true;

      // Recoverable conflicts keep retrying without burning the auto-retry
      // cap (the server message says the sale stays queued until stock is
      // replenished / state is re-read). Permanent conflicts stop after
      // MAX_AUTO_SYNC_ATTEMPTS; manual Sync Now still picks them up.
      if (
        typeof s.lastErrorReason === 'string' &&
        (RECOVERABLE_CONFLICT_REASONS as string[]).includes(s.lastErrorReason)
      ) {
        return true;
      }

      return (s.attempts || 0) < MAX_AUTO_SYNC_ATTEMPTS;
    });
    if (unsynced.length === 0) {
      resetAutoRetry();
      return noop;
    }

    // FIFO replay order — display order (newest first) is not sync order.
    // Tiebreak on localId so rapid double-tap sales created in the same
    // millisecond replay deterministically.
    const ordered = [...unsynced].sort((a: any, b: any) => {
      const timeDiff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      if (timeDiff !== 0) return timeDiff;
      return String(a.localId ?? '').localeCompare(String(b.localId ?? ''));
    });

    emitSyncEvent({ type: 'sync-started' });

    let syncedCount = 0;
    let transportFailed = false;
    let authFailed = false;
    const failedConflicts: any[] = [];
    let lastServerTime: string | undefined;

    // Without getSale, snapshot the queue at most once per chunk: re-reading
    // the whole store for every sale was O(N^2) and issued a fresh readonly
    // transaction while the readwrite drain was in progress.
    let chunkQueuedIds: Set<string> | null = null;
    const isStillQueued = async (localId: string): Promise<boolean> => {
      if (deps.getSale) {
        const s = await deps.getSale(localId).catch(() => null);
        return Boolean(s);
      }
      if (chunkQueuedIds === null) {
        try {
          const queue = await deps.loadQueue();
          // Every other comparison here stringifies ids; a stored non-string
          // id would miss the strict equality and be treated as removed.
          chunkQueuedIds = new Set(queue.map((s) => String(s.localId)));
        } catch {
          return true;
        }
      }
      return chunkQueuedIds.has(String(localId));
    };

    for (let offset = 0; offset < ordered.length; offset += SYNC_BATCH_SIZE) {
      const chunk = ordered.slice(offset, offset + SYNC_BATCH_SIZE);
      chunkQueuedIds = null;

      const salesMap: Record<string, any> = {};
      for (const sale of chunk) {
        salesMap[sale.localId] = sale.data;
      }

      let response: any;
      try {
        response = await deps.syncBatch(salesMap);
      } catch (e: any) {
        const status = typeof e?.status === 'number' ? e.status : undefined;

        // Distinguish a rejected PAYLOAD from a broken transport. A 4xx means
        // the server refused this exact payload, so replaying it unchanged
        // can never succeed: count the attempt (after
        // MAX_AUTO_SYNC_ATTEMPTS the sale stops auto-retrying and shows up
        // for manual resolution). Without this, every 4xx was treated as a
        // transport failure, `attempts` never moved, and the queue retried
        // the same doomed batch on every 'online' event forever.
        // 401/403 (auth/permission), 408/429 (transient) and 5xx/network
        // errors are NOT the payload's fault, so they keep their attempts
        // and stay queued for the next drain.
        const isPayloadRejection =
          status !== undefined &&
          status >= 400 &&
          status < 500 &&
          ![401, 403, 408, 429].includes(status);

        if (import.meta.env.DEV) console.warn('[Offline Sync] batch failed:', e);

        if (isPayloadRejection) {
          const message =
            typeof e?.data?.message === 'string' && e.data.message
              ? e.data.message
              : `Batch rejected by the server (HTTP ${status}).`;

          for (const sale of chunk) {
            if (!(await isStillQueued(sale.localId))) {
              continue;
            }
            const updated = {
              ...sale,
              attempts: (sale.attempts || 0) + 1,
              lastError: message,
              lastErrorReason: 'rejected',
              lastErrorAt: new Date().toISOString(),
            };
            await deps.persistSale(updated);
            failedConflicts.push({ clientId: sale.localId, reason: 'rejected', message });
          }
          // Later chunks may still be valid — keep going instead of
          // abandoning the whole queue behind one rejected batch.
          continue;
        }

        // Whole chunk failed (network / throttle / 5xx) — this chunk and
        // everything after it stay queued; the next online event or manual
        // Sync Now retries. Earlier chunks that already synced are safe
        // (per-sale dedup makes the replay idempotent).
        // 401/403 are surfaced visibly: an expired token used to leave the
        // queue in perpetual "pending" with backoff retries and no failed
        // entry. Stamp a non-burning auth error so the cashier sees WHY, and
        // flag the drain result for the indicator.
        transportFailed = true;
        if (status === 401 || status === 403) {
          authFailed = true;
          const message =
            status === 401
              ? 'Session expired — re-login, then Sync Now. The sale stays queued.'
              : 'Missing POS permission — ask a manager, then Sync Now. The sale stays queued.';
          for (const sale of chunk) {
            if (!(await isStillQueued(sale.localId))) {
              continue;
            }
            await deps.persistSale({
              ...sale,
              lastError: message,
              lastErrorReason: 'auth',
              lastErrorAt: new Date().toISOString(),
            });
          }
          failedConflicts.push({ clientId: chunk.map((s) => s.localId).join(','), reason: 'auth', message });
        }
        break;
      }

      if (typeof response?.serverTime === 'string') {
        lastServerTime = response.serverTime;
      }

      const syncedByClient = new Map<string, any>(
        ((response?.synced as any[]) || []).map((s) => [String(s.clientId), s]),
      );
      const conflictByClient = new Map<string, any>(
        ((response?.conflicts as any[]) || []).map((c) => [String(c.clientId), c]),
      );

      for (const sale of chunk) {
        if (syncedByClient.has(String(sale.localId))) {
          // Confirmed on the server (or deduplicated) — remove from the queue.
          await deps.deleteSale(sale.localId);
          syncedCount++;
        } else if (conflictByClient.has(String(sale.localId))) {
          if (!(await isStillQueued(sale.localId))) {
            continue;
          }
          const conflict = conflictByClient.get(String(sale.localId));
          const updated = {
            ...sale,
            attempts: (sale.attempts || 0) + 1,
            lastError: conflict.message,
            lastErrorReason: conflict.reason,
            // Keep the server's STRUCTURED detail (field-level `errors`,
            // `conflict_details`, `rule`). Persisting only the message left the
            // cashier knowing that something was wrong but not which product
            // or price drifted, so the sale could not be acted on without
            // exporting the raw payload.
            lastErrorErrors: conflict.errors ?? null,
            lastErrorDetails: conflict.conflict_details ?? null,
            lastErrorRule: conflict.rule ?? null,
            lastErrorAt: new Date().toISOString(),
          };
          await deps.persistSale(updated);
          failedConflicts.push(conflict);
        } else {
          if (!(await isStillQueued(sale.localId))) {
            continue;
          }
          // The server answered but named neither outcome for this sale: it was
          // not booked and not explicitly rejected. Previously nothing happened
          // at all — attempts never moved, so the row was replayed on every
          // drain forever with no visible progress. Count it so it reaches the
          // manual-resolution state like any other unresolved conflict.
          const message = 'The server did not report an outcome for this sale.';
          const updated = {
            ...sale,
            attempts: (sale.attempts || 0) + 1,
            lastError: message,
            lastErrorReason: 'unreported',
            lastErrorAt: new Date().toISOString(),
          };
          await deps.persistSale(updated);
          failedConflicts.push({ clientId: sale.localId, reason: 'unreported', message });
        }
      }
    }

    let clockDriftMinutes: number | undefined;
    if (lastServerTime) {
      const serverMs = new Date(lastServerTime).getTime();
      if (!Number.isNaN(serverMs)) {
        const driftMs = Math.abs(Date.now() - serverMs);
        const driftMin = Math.round(driftMs / (60 * 1000));
        if (driftMin > MAX_ALLOWED_CLOCK_DRIFT_MINUTES) {
          clockDriftMinutes = driftMin;
        }
      }
    }

    const result: DrainResult = {
      ran: true,
      syncedCount,
      failedConflicts,
      transportFailed,
      ...(authFailed ? { authFailed: true as const } : {}),
      ...(clockDriftMinutes !== undefined ? { clockDriftMinutes } : {}),
    };

    if (transportFailed || failedConflicts.length > 0) {
      scheduleAutoRetry();
    } else {
      resetAutoRetry();
    }

    emitSyncEvent({ type: 'sync-finished', ...result });
    return result;
  }
}

/**
 * App-wide background drain — mounted by SystemInfrastructureProvider (boot,
 * 'online' events, visibilitychange). Gates on the DEVICE link only; a failed
 * attempt backs off with jitter (scheduleAutoRetry) rather than being blocked
 * up-front by a stale "server down" observation. See the note in the body.
 */
export function startBackgroundSync(): void {
  void (async () => {
    try {
      const { navigatorOnline } = connectivityStore.getState();
      // Only the DEVICE link blocks the drain. `degraded` (link up, server
      // reported down) deliberately does NOT: rtkQueryBaseQuery flips
      // serverReachable to 'down' on ANY 5xx, so one failing endpoint — an
      // unrelated report, say — used to stall the entire offline queue,
      // i.e. money already collected, until the next successful health probe.
      // Draining is self-limiting instead: a failed attempt sets
      // transportFailed and schedules a jittered retry, so a genuinely down
      // server is retried with backoff rather than hammered.
      if (!navigatorOnline) return;
      const userId = await resolveModuleUserId();
      await drainPendingSales({ manual: false, userId, isOnline: true });
    } catch {
      // The background drain must never crash its trigger.
    }
  })();
}

async function resolveModuleUserId(): Promise<any> {
  try {
    const { store } = await import('@/redux/store');
    return (store.getState() as any)?.auth?.user?.id ?? null;
  } catch {
    return null;
  }
}

export function usePosOffline() {
  const { t } = useTranslation();
  const swal = useSwal();
  const { isOnline, wasOffline, onOnline } = useOffline();
  const currentUserId = useAppSelector((state: any) => state.auth?.user?.id);
  const [triggerGetPosProducts] = useLazyGetPosProductsQuery();
  const [triggerGetCustomers] = useLazyGetPosCustomersWithoutPaginateQuery();
  const [triggerGetPosSettings] = useLazyGetPosSettingsQuery();
  const [syncPosSales] = useSyncPosSalesMutation();

  const [isCacheLoaded, setIsCacheLoaded] = useState(false);
  const [isSyncingSales, setIsSyncingSales] = useState(false);
  const [cacheError, setCacheError] = useState<string | null>(null);
  const [lastCacheTime, setLastCacheTime] = useState<number | null>(null);

  const [pendingSales, setPendingSales] = useState<any[]>([]);
  const [syncedSaleCount, setSyncedSaleCount] = useState(0);
  const [failedSaleCount, setFailedSaleCount] = useState(0);

  // Refs mirror the guard state so the window 'online' listener (registered
  // once) never acts on a stale closure. The in-flight guard itself is the
  // module-level `syncInFlight` (this hook is mounted twice on the POS page);
  // manual vs automatic is a per-call drain option now.
  const pendingSalesRef = useRef<any[]>([]);
  const currentUserIdRef = useRef<any>(currentUserId);
  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);

  // Only the signed-in user's queued sales are counted/synced — leftover
  // rows from a previous session must never replay under a new token.
  const myPendingSales = useMemo(
    () => pendingSales.filter((s) => String(s.userId ?? '') === String(currentUserId ?? '')),
    [pendingSales, currentUserId],
  );
  const pendingCount = useMemo(() => myPendingSales.filter((s) => !s.synced).length, [myPendingSales]);

  const dataSource = useMemo(() => (isOnline ? 'live' : 'cached'), [isOnline]);

  // The drain lives at module level (shared with the app-wide background
  // drain); this hook's drain reports through its events — first subscriber
  // claims the toasts so the double mount never doubles notifications.
  useEffect(() => {
    return subscribePosOfflineSync((event) => {
      if (event.type === 'sync-started') {
        setIsSyncingSales(true);
        setSyncedSaleCount(0);
        setFailedSaleCount(0);
        return;
      }
      setIsSyncingSales(false);
      setSyncedSaleCount(event.syncedCount);
      setFailedSaleCount(event.failedConflicts.length);
      void loadPendingSales();
      if (!tryClaimFinishedToasts()) return;
      if (event.syncedCount > 0) {
        swal.toastSuccess(t('Offline_sync_completed', { count: event.syncedCount }));
      }
      if (event.failedConflicts.length > 0) {
        swal.toastError(t('Offline_sync_conflicts', { count: event.failedConflicts.length }));
      }
      if (event.transportFailed) {
        swal.toastError(t('Offline_sync_failed'));
      }
      if (event.clockDriftMinutes !== undefined && event.clockDriftMinutes > MAX_ALLOWED_CLOCK_DRIFT_MINUTES) {
        swal.toastWarning(t('Terminal_clock_drift_warning', { minutes: event.clockDriftMinutes }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function cacheProductsForWarehouse(warehouseId: number | string): Promise<number> {
    if (!isOnline) return 0;
    try {
      const responseData = await triggerGetPosProducts({ warehouseId: warehouseId, limit: 5000 }).unwrap();
      const { products, total } = extractPosProductPage(responseData);
      if (Number(total) > products.length) {
        const message = `[POS] Product catalog truncated for offline cache: cached ${products.length} of ${total} (warehouse ${warehouseId}). Increase the preload limit or paginate the cache.`;
        setCacheError(message);
        if (import.meta.env.DEV) console.warn(message);
      }
      await rewriteWarehouseProducts(products, String(warehouseId));
      await idbPut('settings', { key: 'lastProductCache', value: Date.now() });
      setLastCacheTime(Date.now());
      return products.length;
    } catch (e: any) {
      setCacheError(e.message);
      return 0;
    }
  }

  async function cacheCustomers(): Promise<number> {
    if (!isOnline) return 0;
    try {
      const responseData = await triggerGetCustomers().unwrap();
      const customers = responseData?.data || responseData || [];
      const db = await openCatalogDB();
      const tx = db.transaction('customers', 'readwrite');
      const store = tx.objectStore('customers');
      for (const customer of customers) {
        store.put({ ...customer, cachedAt: Date.now() });
      }
      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        // Without onabort an aborted transaction left this promise pending
        // forever, hanging preloadAll.
        tx.onabort = () => reject(tx.error);
      });
      return customers.length;
    } catch (e: any) {
      setCacheError(e.message);
      return 0;
    }
  }

  /** Best-effort cache of GET /pos/settings (receipt/print preferences). */
  async function cachePosSettings(): Promise<void> {
    if (!isOnline) return;
    try {
      const response = await triggerGetPosSettings().unwrap();
      const data = response?.data ?? response;
      if (data) await putKeyedRow('pos_settings', 'posSettings', data);
    } catch {
      // Not every role can read settings — the receipt composes without it.
    }
  }

  async function getOfflineProducts(warehouseId?: number | string): Promise<any[]> {
    if (warehouseId) {
      return idbGetByIndex('products', 'warehouseId', String(warehouseId));
    }
    return idbGetAll('products');
  }

  async function searchOfflineProducts(query: string, warehouseId?: number | string): Promise<any[]> {
    const all = await getOfflineProducts(warehouseId);
    if (!query) return all;
    const q = query.toLowerCase();
    return all.filter((p: any) =>
      (p.name && p.name.toLowerCase().includes(q)) ||
      (p.code && p.code.toLowerCase().includes(q)) ||
      (p.typeBarcode && p.typeBarcode.toLowerCase().includes(q)) ||
      (p.barcode && p.barcode.toLowerCase().includes(q))
    );
  }

  async function getOfflineCustomers(): Promise<any[]> {
    return idbGetAll('customers');
  }

  async function createOfflineSale(saleData: any, extras?: { receiptData?: any }): Promise<any> {
    // The drain and queue UI filter on userId. Assigning a peer's origin
    // cashier here hid the imported sale from its uploader and prevented
    // replay. The server reads origin attribution from data.user_id instead.
    const sale = createPendingSaleRecord(saleData, currentUserId, extras?.receiptData ?? null);
    try {
      await idbPut('pending_sales', sale);
    } catch (err: any) {
      if (err?.name === 'QuotaExceededError' || err?.code === 22) {
        console.error('[Offline POS] Storage quota exceeded when saving pending sale:', err);
        swal.toastError(t('Storage_quota_exceeded', 'Browser storage is full. Please free space or sync sales.'));
      }
      throw err;
    }
    setPendingSales((prev) => [sale, ...prev]);
    pendingSalesRef.current = [sale, ...pendingSalesRef.current];

    // Local stock decrement: the cached catalog must reflect queued sales or
    // the till happily oversells the same unit to the next customer.
    // Oversell is still queued (cash already collected) but flagged loudly:
    // the floor at 0 hides it, and the server will hold it as
    // insufficient_stock until replenishment.
    try {
      const { oversoldProductIds } = await adjustCachedProductQuantities(
        saleData?.warehouse_id,
        (Array.isArray(saleData?.products) ? saleData.products : []).map((p: any) => ({
          productId: p?.product_id,
          quantity: p?.quantity,
        })),
        -1,
      );
      if (oversoldProductIds.length > 0) {
        sale.hasOversellRisk = true;
        sale.oversoldProductIds = oversoldProductIds;
        try {
          await idbPut('pending_sales', sale);
        } catch {
          // Flag best-effort — the queued row itself already persisted.
        }
        setPendingSales((prev) => prev.map((s: any) => (s.localId === sale.localId ? sale : s)));
        pendingSalesRef.current = pendingSalesRef.current.map((s: any) => (s.localId === sale.localId ? sale : s));
        swal.toastError(t('Offline_oversell_queued', 'Queued with insufficient cached stock — replenish before sync.'));
      }
    } catch {
      // Guard best-effort — queueing the sale itself is what matters.
    }

    return sale;
  }

  async function loadPendingSales(): Promise<void> {
    const sales = await idbGetAll('pending_sales');
    const sorted = sales.sort((a: any, b: any) => {
      const timeDiff = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      if (timeDiff !== 0) return timeDiff;
      return String(b.localId ?? '').localeCompare(String(a.localId ?? ''));
    });
    setPendingSales(sorted);
    pendingSalesRef.current = sorted;
  }

  async function syncPendingSales(manual = false): Promise<void> {
    await drainPendingSales(
      { manual, userId: currentUserIdRef.current, isOnline },
      {
        ...defaultDrainDeps,
        // Route through this instance's RTK mutation when available (same
        // endpoint + cache invalidation as before the module extraction).
        syncBatch: (salesMap) => syncPosSales({ salesMap }).unwrap() as any,
      },
    );
  }

  async function removePendingSale(localId: string): Promise<void> {
    // Read before delete: the discarded sale's lines must be RE-ADDED to the
    // cached stock (its decrement is reversed, or the till stays wrong).
    const row = await idbGet('pending_sales', localId).catch(() => null);
    await idbDelete('pending_sales', localId);
    const next = pendingSalesRef.current.filter((s) => s.localId !== localId);
    setPendingSales(next);
    pendingSalesRef.current = next;
    // Only reverse the cached-stock decrement for a sale that never reached the
    // server. An already-synced row had its stock applied server-side (and the
    // local cache refreshed from it), so re-adding here double-counted.
    if (row?.data?.products && !row?.synced) {
      try {
        await adjustCachedProductQuantities(
          row.data.warehouse_id,
          row.data.products.map((p: any) => ({ productId: p?.product_id, quantity: p?.quantity })),
          1,
        );
      } catch {
        // Restore is best-effort.
      }
    }
  }

  async function initializeCache(): Promise<void> {
    const sales = await idbGetAll('pending_sales');
    // Evict legacy synced rows kept forever by the old replay flow.
    const cutoff = Date.now() - SYNCED_RECORD_RETENTION_MS;
    const kept: any[] = [];
    for (const sale of sales) {
      const syncedAtMs = sale.syncedAt ? new Date(sale.syncedAt).getTime() : null;
      if (sale.synced && syncedAtMs !== null && syncedAtMs < cutoff) {
        await idbDelete('pending_sales', sale.localId);
      } else {
        kept.push(sale);
      }
    }
    const sorted = kept.sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    setPendingSales(sorted);
    pendingSalesRef.current = sorted;
    const settings = await idbGet('settings', 'lastProductCache');
    if (settings) {
      setLastCacheTime(settings.value);
    }
    setIsCacheLoaded(true);
  }

  async function preloadAll(warehouseId: number | string): Promise<{ productCount: number; customerCount: number }> {
    const productCount = await cacheProductsForWarehouse(warehouseId);
    const customerCount = await cacheCustomers();
    await cachePosSettings();
    return { productCount, customerCount };
  }

  // Keep a stable reference for the once-registered online listener.
  const syncRef = useRef(syncPendingSales);
  useEffect(() => {
    syncRef.current = syncPendingSales;
  });

  useEffect(() => {
    // A terminal that reloads while already online never receives the window
    // 'online' event — drain the queue once on mount too, or queued sales
    // would sit idle until the next offline→online transition or a manual
    // Sync Now. (drainPendingSales re-hydrates from the queue store itself,
    // so an early call before initializeCache is a harmless no-op.)
    if (isOnline) {
      void drainPendingSales({ manual: false, userId: currentUserIdRef.current, isOnline });
    }

    return onOnline(() => {
      syncRef.current();
    });
  }, [onOnline, isOnline]);

  return {
    isOnline,
    wasOffline,
    dataSource,
    isCacheLoaded,
    setIsCacheLoaded,
    isSyncingSales,
    setIsSyncingSales,
    cacheError,
    setCacheError,
    lastCacheTime,
    setLastCacheTime,

    pendingSales,
    setPendingSales,
    myPendingSales,
    pendingCount,
    syncedSaleCount,
    setSyncedSaleCount,
    failedSaleCount,
    setFailedSaleCount,

    cacheProductsForWarehouse,
    cacheCustomers,
    cachePosSettings,
    cachePosElements,
    preloadAll,
    initializeCache,

    getOfflineProducts,
    searchOfflineProducts,
    getOfflineCustomers,

    createOfflineSale,
    syncPendingSales,
    removePendingSale,
    loadPendingSales,
  };
}

export default usePosOffline;
